import { gql, useQuery } from "@apollo/client";
import { TextField, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useFlags } from "@resource/client-ffs";
import emailValidator from "email-validator";
import FeatureFlags from "generated/FeatureFlags";
import { GreenhouseUsers } from "generated/schemaTypes";
import _ from "lodash";
import { useState } from "react";
import hackUpdaterAgencyList from "utils/constants/hack-updater-agency-recruiters";

const ORGANIZATION_GREENHOUSE_USERS = gql`
  query GreenhouseUsers {
    atsUsers {
      id
      name
      primaryEmailAddress
    }
  }
`;

type EmailValue = {
  createdEmail?: string;
  email: string;
  name?: string;
};

type GreenhouseUserEmailSelectProps = {
  label?: string;
  emails: string[];
  maxEmails?: number;
  className?: string;
  onChange(emails: string[]): void;
};

type AgencyUser = {
  Agency: string;
  Name: string;
  Email: string;
};

function GreenhouseUserEmailSelect({
  emails,
  maxEmails,
  onChange,
  className = "",
  label = "Emails",
}: GreenhouseUserEmailSelectProps) {
  const { [FeatureFlags.UPDATER_AGENCY_LIST]: updaterAgencyList } = useFlags();

  // Hack in Updater Agency Values
  const agencyRecruiterUsers = updaterAgencyList
    ? _.map(hackUpdaterAgencyList, (agencyUser: AgencyUser) => ({
        name: `${agencyUser.Name} (${agencyUser.Agency})`,
        email: agencyUser.Email,
      }))
    : [];

  const { data, loading } = useQuery<GreenhouseUsers>(
    ORGANIZATION_GREENHOUSE_USERS
  );
  const userValues = _(data?.atsUsers)
    .map((user) => ({
      name: user.name,
      email: user.primaryEmailAddress,
    }))
    .concat(agencyRecruiterUsers)
    .sortBy("name")
    .value();

  const selectedValues = _.map(emails, (e) => ({
    name: _.find(userValues, { email: e })?.name,
    email: e,
  }));

  const [error, setError] = useState<string>("");

  return (
    <div className={className}>
      <Autocomplete<EmailValue, true, true, false>
        multiple
        limitTags={maxEmails ?? -1}
        value={selectedValues}
        options={userValues}
        filterSelectedOptions
        getOptionSelected={(option, value) => option.email === value.email}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete="off"
            label={loading ? "Loading…" : label}
            placeholder={emails.length ? "" : "Start typing to add an email"}
            variant="outlined"
            fullWidth
          />
        )}
        ChipProps={{
          color: "primary",
        }}
        getOptionLabel={(option) =>
          option.name ? `${option.name} (${option.email})` : option.email
        }
        filterOptions={(options, params) => {
          if (params.inputValue !== "") {
            return [
              {
                createdEmail: params.inputValue,
                email: `Add "${params.inputValue}"`,
              },
              ..._.filter(
                options,
                (o) =>
                  _.includes(o.name, params.inputValue) ||
                  _.includes(o.email, params.inputValue)
              ),
            ];
          }

          return options;
        }}
        onChange={(_event, newValues) => {
          const isCreated = (v: EmailValue) => !!v.createdEmail;
          const createdEmail = _.find(newValues, isCreated)?.createdEmail;
          if (createdEmail) {
            if (!emailValidator.validate(createdEmail)) {
              setError("Please enter a valid email address.");
              return undefined;
            }
            onChange(
              _(newValues)
                .reject(isCreated)
                .map("email")
                .concat(createdEmail.toLowerCase())
                .value()
            );
          } else {
            onChange(_.map(newValues, "email"));
          }

          setError("");
          return undefined;
        }}
      />
      {error && (
        <Typography variant="subtitle1" color="error">
          {error}
        </Typography>
      )}
    </div>
  );
}

export default GreenhouseUserEmailSelect;
